import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ImageIcon, Plus, X } from 'lucide-react';
import { cn } from '../../../lib/utils';

// Form controls for the admin editors (blog posts, case studies). Same tokens
// as the Catalogue toolbar: hairline --oa-line-2 borders, green focus ring, 13px text.

const control =
  'w-full rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-2.5 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none transition-colors focus:border-[color:var(--oa-accent)] focus:ring-2 focus:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]';

export const Field: React.FC<{ label: React.ReactNode; hint?: React.ReactNode; htmlFor?: string; className?: string; children: React.ReactNode }> = ({
  label,
  hint,
  htmlFor,
  className,
  children,
}) => (
  <div className={cn('space-y-1.5', className)}>
    <label htmlFor={htmlFor} className="block text-[12px] font-medium text-[color:var(--oa-text)]">
      {label}
    </label>
    {children}
    {hint ? <p className="text-[11.5px] leading-snug text-[color:var(--oa-muted)]">{hint}</p> : null}
  </div>
);

export const TextInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { prefix?: string }>(
  ({ className, prefix, ...rest }, ref) =>
    prefix ? (
      <div className="flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] transition-colors focus-within:border-[color:var(--oa-accent)] focus-within:ring-2 focus-within:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]">
        <span className="flex items-center border-r border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2 font-mono text-[11px] text-[color:var(--oa-muted)]">{prefix}</span>
        <input ref={ref} {...rest} className={cn('min-w-0 flex-1 px-2 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none', className)} />
      </div>
    ) : (
      <input ref={ref} {...rest} className={cn(control, 'h-8', className)} />
    )
);
TextInput.displayName = 'TextInput';

/** Textarea that grows with its content. */
export const AutoTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number; bare?: boolean }> = ({
  className,
  minRows = 3,
  bare,
  value,
  ...rest
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      {...rest}
      className={cn(bare ? 'w-full resize-none bg-transparent outline-none placeholder:text-[color:var(--oa-faint)]' : cn(control, 'resize-none py-2 leading-relaxed'), className)}
    />
  );
};

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, children, ...rest }) => (
  <div className="relative">
    <select {...rest} className={cn(control, 'h-8 appearance-none pr-7', className)}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[color:var(--oa-muted)]" />
  </div>
);

export const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode; hint?: React.ReactNode }> = ({ checked, onChange, label, hint }) => (
  <label className="flex cursor-pointer items-start justify-between gap-3">
    <span>
      <span className="block text-[12.5px] font-medium text-[color:var(--oa-text)]">{label}</span>
      {hint ? <span className="mt-0.5 block text-[11.5px] text-[color:var(--oa-muted)]">{hint}</span> : null}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      // The Splitfin switch: tinted track, and a knob that springs across and turns green when on.
      className={cn(
        'relative mt-0.5 h-[22px] w-[38px] shrink-0 rounded-full border transition-colors duration-300',
        checked ? 'border-[color:color-mix(in_oklab,var(--oa-accent)_40%,transparent)] bg-[color:var(--oa-accent-soft)]' : 'border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)]'
      )}
    >
      <span
        className={cn('absolute left-[2px] top-[2px] h-4 w-4 rounded-full transition-[transform,background-color] duration-[450ms] ease-[cubic-bezier(.2,.9,.25,1.15)]', checked ? 'translate-x-4 bg-[color:var(--oa-accent)]' : 'translate-x-0 bg-[color:var(--oa-muted)]')}
      />
    </button>
  </label>
);

/** Chip list with an inline "type and press Enter" input. */
export const TagInput: React.FC<{ value: string[]; onChange: (v: string[]) => void; placeholder?: string }> = ({ value, onChange, placeholder = 'Add and press Enter' }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft('');
  };
  return (
    <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-1.5 py-1 transition-colors focus-within:border-[color:var(--oa-accent)] focus-within:ring-2 focus-within:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]">
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded bg-[color:var(--oa-raised-2)] py-0.5 pl-1.5 pr-0.5 text-[12px] text-[color:var(--oa-text)]">
          {t}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="rounded p-0.5 text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-line-2)] hover:text-[color:var(--oa-text)]" aria-label={`Remove ${t}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={value.length ? '' : placeholder}
        className="h-6 min-w-[80px] flex-1 bg-transparent px-1 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none"
      />
    </div>
  );
};

/** Image URL field with a live preview. The editors take URLs; they do not upload. */
export const ImageField: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; aspect?: string }> = ({
  value,
  onChange,
  placeholder = 'https://…',
  aspect = 'aspect-[16/9]',
}) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="space-y-2">
      <div className={cn('relative overflow-hidden rounded-lg border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)]', aspect)}>
        {value && !broken ? (
          <img src={value} alt="" onError={() => setBroken(true)} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[color:var(--oa-faint)]">
            <ImageIcon className="h-6 w-6" />
            <span className="text-[11px]">{value && broken ? 'Could not load that image' : 'No image'}</span>
          </div>
        )}
      </div>
      <TextInput
        value={value}
        onChange={(e) => {
          setBroken(false);
          onChange(e.target.value);
        }}
        placeholder={placeholder}
      />
    </div>
  );
};

/** One section of the right-hand inspector. */
export const InspectorSection: React.FC<{ title: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-[color:var(--oa-line-2)] last:border-b-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[color:var(--oa-muted)]">{title}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-[color:var(--oa-muted)] transition-transform', !open && '-rotate-90')} />
      </button>
      {open ? <div className="space-y-3.5 px-4 pb-4">{children}</div> : null}
    </section>
  );
};

/** Small text button used for "Add item" rows. */
export const AddRowButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-[12.5px] font-medium text-[color:var(--oa-accent)] transition-colors hover:bg-[color:var(--oa-accent-soft)]"
  >
    <Plus className="h-3.5 w-3.5" />
    {children}
  </button>
);

/** Square icon button for row-level remove/move actions. */
export const IconButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'default' | 'danger' }> = ({ tone = 'default', className, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={cn(
      'grid h-7 w-7 place-items-center rounded-md text-[color:var(--oa-muted)] transition-colors disabled:opacity-40',
      tone === 'danger' ? 'hover:bg-[color:color-mix(in_oklab,var(--oa-bad)_12%,transparent)] hover:text-[color:var(--oa-bad)]' : 'hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)]',
      className
    )}
  >
    {children}
  </button>
);
